import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useSwipeable } from 'react-swipeable';
import { FaHeart, FaRegHeart, FaExpand, FaCompress, FaTimes, FaArrowLeft, FaArrowRight, FaSignInAlt } from 'react-icons/fa';
import { MdPhotoCamera } from 'react-icons/md';
import { useTranslation } from 'react-i18next';

const AccessibleLightbox = ({
  photos,
  startIndex = 0,
  isOpen,
  onClose,
  onLike,
  user,
}) => {
  const { t } = useTranslation();
  const [photoIndex, setPhotoIndex] = useState(startIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // Reset photoIndex when the startIndex prop changes
  useEffect(() => {
    setPhotoIndex(startIndex);
  }, [startIndex]);
  
  // Hide header and footer when lightbox is open
  useEffect(() => {
    if (isOpen) {
      // Hide header and footer when lightbox is open
      const header = document.querySelector('.main-header');
      const footer = document.querySelector('.main-footer');
      
      if (header) header.style.display = 'none';
      if (footer) footer.style.display = 'none';
      
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
      
      // Set full screen mode for mobile browsers if available
      if (document.documentElement.requestFullscreen && window.innerWidth < 768) {
        try {
          document.documentElement.requestFullscreen().catch(() => {
            // Silently fail if fullscreen is not available or allowed
          });
        } catch (e) {
          // Ignore fullscreen API errors
        }
      }
    } else {
      // Restore header and footer when lightbox is closed
      const header = document.querySelector('.main-header');
      const footer = document.querySelector('.main-footer');
      
      if (header) header.style.display = '';
      if (footer) footer.style.display = '';
      
      // Re-enable body scrolling
      document.body.style.overflow = '';
      
      // Exit fullscreen mode if we're in it
      if (document.fullscreenElement && document.exitFullscreen) {
        try {
          document.exitFullscreen().catch(() => {
            // Silently fail if exit fullscreen fails
          });
        } catch (e) {
          // Ignore fullscreen API errors
        }
      }
    }
    
    // Cleanup function
    return () => {
      const header = document.querySelector('.main-header');
      const footer = document.querySelector('.main-footer');
      
      if (header) header.style.display = '';
      if (footer) footer.style.display = '';
      document.body.style.overflow = '';
      
      // Exit fullscreen mode if we're in it
      if (document.fullscreenElement && document.exitFullscreen) {
        try {
          document.exitFullscreen().catch(() => {});
        } catch (e) {
          // Ignore fullscreen API errors
        }
      }
    };
  }, [isOpen]);

  // Get current photo data
  const currentPhoto = photos && photos.length > 0 ? photos[photoIndex] : null;

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          movePrev();
          break;
        case 'ArrowRight':
          moveNext();
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, photoIndex, photos]);

  // Handle swipe with improved configuration for better mobile experience
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => moveNext(),
    onSwipedRight: () => movePrev(),
    trackMouse: true,
    trackTouch: true,
    preventDefaultTouchmoveEvent: true,
    delta: 10, // Minimum distance in px before a swipe is registered
    swipeDuration: 250, // Maximum time in ms for a swipe
  });

  const movePrev = () => {
    if (!photos || photos.length === 0) return;
    setPhotoIndex((photoIndex + photos.length - 1) % photos.length);
    setIsZoomed(false);
  };

  const moveNext = () => {
    if (!photos || photos.length === 0) return;
    setPhotoIndex((photoIndex + 1) % photos.length);
    setIsZoomed(false);
  };

  // Function to handle redirect to login with return URL
  const redirectToLogin = useCallback(() => {
    // Store the current URL to return after login
    const currentPath = window.location.pathname;
    sessionStorage.setItem('returnUrl', currentPath);
    
    // Show login modal instead of immediate redirect
    setShowLoginModal(true);
  }, []);
  
  // Handle confirmation to proceed to login
  const handleConfirmLogin = useCallback(() => {
    setShowLoginModal(false);
    // Close the lightbox first to prevent UI issues when returning
    onClose();
    // Short delay to ensure lightbox closes cleanly before redirect
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }, [onClose]);
  
  // Handle cancellation of login
  const handleCancelLogin = useCallback(() => {
    setShowLoginModal(false);
  }, []);

  const handleToggleLike = useCallback(() => {
    if (!user) {
      // Redirect to login if not authenticated
      redirectToLogin();
      return;
    }
    
    if (!currentPhoto) return;
    
    // Pass the photo ID and current like state to parent component's handler
    onLike(currentPhoto.id, currentPhoto.isLiked);
  }, [user, currentPhoto, onLike, redirectToLogin]);

  if (!isOpen || !photos || photos.length === 0 || !currentPhoto) {
    return null;
  }

  return (
    <Modal 
      show={isOpen} 
      onHide={onClose} 
      centered 
      fullscreen
      contentClassName="lightbox-modal-content"
      dialogClassName="lightbox-modal-dialog"
      style={{ zIndex: 2050 }}
    >
      <div className="lightbox-container" {...swipeHandlers}>
        {/* Navigation controls */}
        <div className="lightbox-nav">
          <Button 
            variant="dark" 
            className="nav-btn prev-btn" 
            onClick={movePrev}
            aria-label="Previous photo"
          >
            <FaArrowLeft />
          </Button>
          <Button 
            variant="dark" 
            className="nav-btn next-btn" 
            onClick={moveNext}
            aria-label="Next photo"
          >
            <FaArrowRight />
          </Button>
        </div>
        
        {/* Close button */}
        <Button 
          variant="dark" 
          className="close-lightbox-btn" 
          onClick={onClose}
          aria-label="Close lightbox"
        >
          <FaTimes />
        </Button>
        
        {/* Main image with improved handling */}
        <div className={`lightbox-image-container ${isZoomed ? 'zoomed' : ''}`}>
          <img 
            src={currentPhoto.imageUrl} 
            alt={currentPhoto.description || t('photoGallery.photoBy', { name: currentPhoto.userName })}
            className="lightbox-image"
            loading="eager"
            onClick={() => setIsZoomed(!isZoomed)}
            onError={(e) => {
              // If image fails to load, show a fallback message
              e.target.onerror = null; 
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNGOEY5RkEiIGQ9Ik0wIDBoMjAwdjIwMEgweiIvPjxnIGZpbGw9IiM2Qzc1N0QiPjxwYXRoIGQ9Ik04NSA4OWgzMHYzMkg4NXptNTUgNjZINjB2LThsNDEtMzggMjEgMTggMTgtMTZ6Ii8+PHBhdGggZD0iTTEzMCA2NUgzMGMtMi4yIDAtNCAxLjgtNCA0djEwMmMwIDIuMiAxLjggNCA0IDRoMTQwYzIuMiAwIDQtMS44IDQtNFY2OWMwLTIuMi0xLjgtNC00LTR6TTMwIDU1aDEwMGMxMS4wNDYgMCAyMCA4Ljk1NCAyMCAyMHYxMDJjMCAxMS4wNDYtOC45NTQgMjAtMjAgMjBIMzBjLTExLjA0NiAwLTIwLTguOTU0LTIwLTIwVjc1YzAtMTEuMDQ2IDguOTU0LTIwIDIwLTIweiIgZmlsbC1ydWxlPSJub256ZXJvIi8+PC9nPjwvZz48L3N2Zz4=';
              e.target.classList.add('failed-image');
            }}
          />
        </div>
        
        {/* Photo info and actions */}
        <div className="lightbox-footer">
          <div className="photo-info">
            <h4 className="photographer-credit">
              <MdPhotoCamera className="camera-icon" />
              <span className="photographer-name">© {currentPhoto.userName || 'User'}</span>
            </h4>
            <p className="description">{currentPhoto.description || t('photoGallery.noDescription')}</p>
            <p className="date">
              {currentPhoto.uploadedAt ? 
                `${new Date(currentPhoto.uploadedAt).toLocaleDateString()} ${new Date(currentPhoto.uploadedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` 
                : ''}
            </p>
          </div>
          <div className="photo-actions">
            <Button 
              variant="link" 
              className="like-btn p-0" 
              onClick={() => user ? handleToggleLike() : redirectToLogin()}
              aria-label={currentPhoto.isLiked ? "Unlike photo" : "Like photo"}
            >
              {currentPhoto.isLiked ? 
                <FaHeart className="text-danger" size={24} /> : 
                <FaRegHeart className="text-white" size={24} />
              }
              <span className="like-count">{currentPhoto.likeCount || 0}</span>
            </Button>
            <Button 
              variant="link" 
              className="zoom-btn p-0" 
              onClick={() => setIsZoomed(!isZoomed)}
              aria-label={isZoomed ? "Zoom out" : "Zoom in"}
            >
              {isZoomed ? <FaCompress size={20} /> : <FaExpand size={20} />}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Login Confirmation Modal */}
      <Modal
        show={showLoginModal}
        onHide={handleCancelLogin}
        centered
        size="sm"
        style={{ zIndex: 2100 }} // Higher than lightbox modal
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('loginRequired')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('loginToLikeMessage')}</p>
          <div className="text-center mt-3">
            <a 
              href="/terms-policy" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="terms-link"
              onClick={() => sessionStorage.setItem('returnToAfterTerms', window.location.pathname)}
            >
              {t('viewTermsPolicy')}
            </a>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelLogin}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirmLogin}>
            <FaSignInAlt className="me-2" /> {t('login')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default AccessibleLightbox;